<script>
export default {
  name: "DidoDatasetDownload",
};
</script>

<script setup>
import { ref, watch, computed } from "vue";
import JSZip from "jszip";
import api from "../../api";
import { pause, downloadBlob } from "../../utils";

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  dataset: { type: Object, required: true },
});

// define reactive data for flag zip in progress
const zipInProgress = ref("no");
// define computed data for label during zip in progress
const inProgressLabel = computed(() => {
  if (zipInProgress.value === "getFiles")
    return "Récupération des fichiers sélectionnés";
  if (zipInProgress.value === "createZip") return "Construction du fichier zip";
  if (zipInProgress.value === "downloadBegins")
    return "Votre téléchargement va commencer";
  return "";
});

// define computed data for last millesimes
const lastMillesimes = computed(() => {
  if (!props.dataset) return [];
  return props.dataset.datafiles
    .map((datafile) => {
      const publishedMillesimesSorted = datafile.millesimes
        .filter(
          ({ date_diffusion: dateDiffusion }) =>
            new Date() > new Date(dateDiffusion),
        )
        .map(({ millesime }) => millesime)
        .sort((m1, m2) => new Date(m1) - new Date(m2))
        .reverse();
      const lastMillesime = publishedMillesimesSorted.length
        ? publishedMillesimesSorted[0]
        : null;
      return {
        rid: datafile.rid,
        title: datafile.title,
        description: datafile.description,
        millesime: lastMillesime,
        url: lastMillesime
          ? `${api.urlDatafileBase(
              props.apiUrl,
              datafile.rid,
            )}/csv?millesime=${lastMillesime}&withColumnName=true&withColumnDescription=true&withColumnUnit=false`
          : null,
      };
    })
    .filter(({ millesime }) => millesime)
    .map((m) => ({
      ...m,
      selected: !!selectedLinks.value.find((link) => link === m.url),
    }))
    .sort((m1, m2) => (m1.title > m2.title ? -1 : 1));
});
// define computed data for documentations
const documentations = computed(() => {
  if (!props.dataset) return [];
  return props.dataset.attachments
    .filter(({ type }) => !type || type === "documentation")
    .map((doc) => ({
      ...doc,
      selected: !!selectedLinks.value.find((link) => link === doc.url),
    }))
    .sort((doc1, doc2) => (doc1.title > doc2.title ? -1 : 1));
});
// define computed data for historical data
const historicalDatas = computed(() => {
  if (!props.dataset) return [];
  return props.dataset.attachments
    .filter(({ type }) => type === "historical_data")
    .map((doc) => ({
      ...doc,
      selected: !!selectedLinks.value.find((link) => link === doc.url),
    }))
    .sort((doc1, doc2) => (doc1.title > doc2.title ? -1 : 1));
});

// define computed data for defaultLinks
const defaultLinks = computed(() => {
  return [
    ...(documentations.value && documentations.value.length
      ? documentations.value
      : []),
    ...(lastMillesimes.value && lastMillesimes.value.length
      ? lastMillesimes.value
      : []),
  ].map(({ url }) => url);
});

// define reactive data for selected links
const selectedLinks = ref([]);

// define computed data for label of download button
const buttonLabel = computed(() => {
  if (!selectedLinks.value.length) return "Aucun fichier dans l'archive";
  if (selectedLinks.value.length === 1)
    return "Télécharger l'archive avec 1 fichier";
  return `Télécharger l'archive avec ${selectedLinks.value.length} fichiers`;
});

// define watchers
watch(
  () => props.dataset,
  () => init(),
);

// define methods
const init = () => {
  resetSelection();
  zipInProgress.value = "no";
};
const resetSelection = () => {
  selectedLinks.value = defaultLinks.value;
};
const checkAll = () => {
  selectedLinks.value = [
    ...lastMillesimes.value,
    ...documentations.value,
    ...historicalDatas.value,
  ].map(({ url }) => url);
};
const uncheckAll = () => {
  selectedLinks.value = [];
};
const updateSelectedLinks = (selected, url) => {
  if (selected) {
    if (!selectedLinks.value.find((link) => url === link))
      selectedLinks.value = [...selectedLinks.value, url];
  } else {
    selectedLinks.value = selectedLinks.value.filter((link) => url !== link);
  }
};
const downloadZip = async () => {
  // step get files on server
  zipInProgress.value = "getFiles";
  const zip = new JSZip();
  const promisesGetFiles = [
    ...selectedLinks.value.map(
      (link) =>
        new Promise((resolve, reject) =>
          api
            .getNameAndDataFromLink(link)
            .catch(() => reject(`Error during downloading ${link}`))
            .then(({ name, data }) => zip.file(name, data))
            .catch(() => reject(`Error during adding ${link} to zip`))
            .then(() => resolve(true)),
        ),
    ),
    pause(2000),
  ];

  const getFilesResult = await Promise.all(promisesGetFiles).catch(
    (e) => !!console.log("Error during downloading of files", e),
  );

  if (!getFilesResult) {
    zipInProgress.value = "no";
    return;
  }

  // step create archive
  zipInProgress.value = "createZip";
  const promisesCreateZip = [
    zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 9 },
      comment: "Archive created by DiDo Widget",
    }),
    pause(2000),
  ];

  const createZipResult = await Promise.all(promisesCreateZip).catch(
    (e) => !!console.log("Error during zip generation", e),
  );

  if (!createZipResult) {
    zipInProgress.value = "no";
    return;
  }

  // step download zip file
  zipInProgress.value = "downloadBegins";
  await pause(2000);
  downloadBlob(createZipResult[0], "dido.zip");

  zipInProgress.value = "no";
};

init();
</script>

<template>
  <div>
    <div class="text-small">
      Vous pouvez télécharger les fichiers contenus dans ce jeu de données dans
      une archive zip.
      <br />
      <span class="text-italic text-info"
        >Pour cela faites une sélection des fichiers à inclure dans votre
        archive en cochant les cases.</span
      >
    </div>
    <div class="q-pa-sm">
      <QBtn
        size="sm"
        outline
        square
        no-caps
        color="secondary"
        :disable="!selectedLinks.length"
        @click="downloadZip"
      >
        {{ buttonLabel }}
      </QBtn>
      <span
        class="dido-dataset-download-selectaction text-small cursor-pointer on-right"
        title="Séléctionner tous les fichiers"
        @click="checkAll"
        >Tout cocher</span
      >
      <span
        class="dido-dataset-download-selectaction text-small cursor-pointer on-right"
        title="Vider la sélection des fichiers"
        @click="uncheckAll"
        >Tout décocher</span
      >
    </div>
    <QList dense>
      <!-- Last Millesimes with csv format -->
      <template v-if="lastMillesimes.length">
        <QItemLabel header
          >Dernier millésime des fichiers de données au format csv</QItemLabel
        >
        <QItem
          v-for="(millesimeObject, index) in lastMillesimes"
          :key="`millesime-${index}`"
          dense
          :inset-level="1"
        >
          <QItemSection side>
            <QCheckbox
              :model-value="millesimeObject.selected"
              size="xs"
              dense
              color="secondary"
              @update:model-value="
                (selected) => updateSelectedLinks(selected, millesimeObject.url)
              "
            />
          </QItemSection>
          <QItemSection>
            <QItemLabel>{{ millesimeObject.title }}</QItemLabel>
            <QItemLabel caption lines="1"
              >Millésime {{ millesimeObject.millesime }}</QItemLabel
            >
          </QItemSection>
        </QItem>
      </template>
      <!-- Documentations -->
      <template v-if="documentations.length">
        <QItemLabel header>Documentations</QItemLabel>
        <QItem
          v-for="(doc, index) in documentations"
          :key="`doc-${index}`"
          dense
          :inset-level="1"
        >
          <QItemSection side>
            <QCheckbox
              :model-value="doc.selected"
              size="xs"
              dense
              color="secondary"
              @update:model-value="
                (selected) => updateSelectedLinks(selected, doc.url)
              "
            />
          </QItemSection>
          <QItemSection>
            <QItemLabel>{{ doc.title }}</QItemLabel>
          </QItemSection>
        </QItem>
      </template>
      <!-- Historical Datas -->
      <template v-if="historicalDatas.length">
        <QItemLabel header>Données historiques</QItemLabel>
        <QItem
          v-for="(doc, index) in historicalDatas"
          :key="`doc-${index}`"
          dense
          :inset-level="1"
        >
          <QItemSection side>
            <QCheckbox
              :model-value="doc.selected"
              size="xs"
              dense
              color="secondary"
              @update:model-value="
                (selected) => updateSelectedLinks(selected, doc.url)
              "
            />
          </QItemSection>
          <QItemSection>
            <QItemLabel>{{ doc.title }}</QItemLabel>
          </QItemSection>
        </QItem>
      </template>
    </QList>
    <QInnerLoading :showing="zipInProgress !== 'no'" :label="inProgressLabel" />
  </div>
</template>

<style lang="sass" scoped>
.dido-dataset-download-selectaction:hover
  text-decoration: underline
</style>
